import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useSnackbar } from "../contexts/SnackbarContext";
import { trackErrorEvent } from "../utils/analytics";
import { convertIsoDates } from "../utils/dateUtils";
import { formatPersonFullName } from "../utils/formatters";
import { ConflictErrors, type Nurse, NurseActionsErrorTypes } from "../utils/models";
import { departmentRosterQueryKey } from "./nursesQueryKeys";
import { useLastNameFirst } from "./useNameFormat";

const ERROR_AUTO_HIDE_MS = 6_000;
const DETAILED_ERROR_AUTO_HIDE_MS = 10_000;

type ConflictShiftDetail = {
	nurse_id: number | string;
	start_date: DateTime;
	recurring_shift_id?: string;
};

type ServerErrorDetail = {
	error_type?: string;
	message: string | ConflictShiftDetail;
	/** Interpolation params for the message key, so a refusal can name the row that caused it. */
	details?: Record<string, string | number>;
};

export type ShowErrorSnackbar = (errorMessage: string, trackErrorVals: object, error?: unknown) => void;

const readServerErrorDetail = (error: unknown): ServerErrorDetail | undefined => {
	// A thrown axios error carries the server's body here; anything else has no detail to read.
	const detail = (error as { response?: { data?: { detail?: unknown } } } | undefined)?.response?.data?.detail;
	if (detail === undefined || detail === null) return undefined;
	const parsed = typeof detail === "string" ? JSON.parse(detail) : detail;
	return convertIsoDates(parsed) as ServerErrorDetail;
};

/**
 * Shows an error snackbar and tracks it. When the server sent a known error type, its message replaces
 * `errorMessage`: an overlapping-shift conflict names the nurse, other known types are translated keys.
 */
export const useErrorSnackbar = (): ShowErrorSnackbar => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useChosenDepartment();
	const queryClient = useQueryClient();
	const lastNameFirst = useLastNameFirst();

	const conflictMessage = useCallback(
		(detail: ConflictShiftDetail): string => {
			// The conflict carries only the nurse id; the roster this page loaded (if any) names them.
			const roster = queryClient.getQueryData<Nurse[]>(departmentRosterQueryKey(chosenDepartment.department_id));
			const nurse = roster?.find((n) => n.id === Number(detail.nurse_id));
			const day = detail.start_date.toFormat("cccc");
			const date = detail.start_date.toFormat("hh a MMM d, yyyy");
			const isRecurring = Boolean(detail.recurring_shift_id);

			if (!nurse) {
				return isRecurring ? t("error.recurring.shift.overlap.unnamed", { day, date }) : t("error.nurse.two.shifts.unnamed", { date });
			}
			const nurseName = formatPersonFullName(nurse, lastNameFirst);
			return isRecurring ? t("error.recurring.shift.overlap", { nurseName, day, date }) : t("error.nurse.two.shifts", { nurseName, day, date });
		},
		[queryClient, chosenDepartment.department_id, lastNameFirst, t],
	);

	const translatedKeyMessage = useCallback(
		(message: string, details: ServerErrorDetail["details"]): string => {
			const [key, status] = message.split("|");
			if (status) return t(key, { status: t(`status.${status.toLowerCase()}`) });
			// `details` doubles as i18n interpolation params so a backend refusal can name the row that caused
			// it — e.g. `shift.nurse.has.no.job`'s `{{nurseNames}}`. Most errors carry none.
			return details ? t(key, details) : t(key);
		},
		[t],
	);

	return useCallback(
		(errorMessage, trackErrorVals, error) => {
			let message = errorMessage;
			let autoHideDuration = ERROR_AUTO_HIDE_MS;

			if (error) {
				console.warn(error);
				try {
					const detail = readServerErrorDetail(error);
					switch (detail?.error_type) {
						case ConflictErrors.ConflictShift:
							message = detail.message && typeof detail.message === "object" ? conflictMessage(detail.message) : t("error.adding.shifts");
							autoHideDuration = DETAILED_ERROR_AUTO_HIDE_MS;
							break;
						case NurseActionsErrorTypes.DuplicateReplaceRequest:
						case NurseActionsErrorTypes.IntegrityError:
						case NurseActionsErrorTypes.UIVisibleError:
						case NurseActionsErrorTypes.MissingNurseEmailPhone:
							if (typeof detail.message === "string" && detail.message) {
								message = translatedKeyMessage(detail.message, detail.details);
								autoHideDuration = DETAILED_ERROR_AUTO_HIDE_MS;
							} else {
								trackErrorEvent("snackbar_error_detail_unreadable", error, { errorMessage, errorType: detail.error_type });
							}
							break;
						case NurseActionsErrorTypes.RequestNotActive:
							message = t("error.request.not.active");
							break;
					}
				} catch (parseError) {
					// The caller's message still shows; the unreadable server detail is reported, not swallowed.
					trackErrorEvent("snackbar_error_detail_unreadable", parseError, { errorMessage });
				}
			}

			trackErrorEvent("snackbarError", error, { ...trackErrorVals, errorMessage: message });
			showSnackbar(message, "error", autoHideDuration);
		},
		[conflictMessage, translatedKeyMessage, showSnackbar, t],
	);
};
